import { pt } from '../layout';
import type { Selection } from '../types';
import type { GroupEntry } from '../layout';

interface HandleProps {
  kind: 'disc' | 'ring';
  ox: number;
  oy: number;
  r: number;
  angDeg: number;
  target: string;
  gi?: number;
  pi?: number;
  label: string;
}

function Handle({ kind, ox, oy, r, angDeg, target, gi, pi, label }: HandleProps) {
  const p = pt(r, angDeg, ox, oy);
  return (
    <>
      <circle className="hdl-line" cx={ox} cy={oy} r={r} />
      <text className="hdl-lbl" x={p.x} y={p.y - 12}>{label}</text>
      <g
        className={`hdl ${kind}`}
        tabIndex={-1}
        data-hkind={kind}
        data-target={target}
        data-gi={gi}
        data-pi={pi}
      >
        {kind === 'disc'
          ? <rect x={p.x - 7} y={p.y - 7} width={14} height={14} rx={3} />
          : <circle cx={p.x} cy={p.y} r={7} />}
      </g>
    </>
  );
}

interface ResizeHandlesProps {
  selection: Selection;
  layout: GroupEntry[];
  sunX: number;
  sunY: number;
  sunR: number;
}

export default function ResizeHandles({ selection, layout, sunX, sunY, sunR }: ResizeHandlesProps) {
  if (!selection) return null;

  if (selection.kind === 'group') {
    const ge = layout[selection.gi];
    if (!ge) return null;
    return (
      <>
        <Handle kind="disc" ox={ge.gcx} oy={ge.gcy} r={ge.discR} angDeg={-45}
          target="gdisc" gi={selection.gi} label="SIZE" />
        {ge.solo && (
          <Handle kind="ring" ox={ge.gcx} oy={ge.gcy} r={ge.progs[0].ring} angDeg={0}
            target="gring" gi={selection.gi} pi={0} label="ORBIT" />
        )}
      </>
    );
  }

  if (selection.kind === 'prog') {
    const ge = layout[selection.gi];
    const pe = ge && ge.progs[selection.pi];
    if (!pe) return null;
    return (
      <>
        <Handle kind="disc" ox={pe.pcx} oy={pe.pcy} r={pe.discR} angDeg={-45}
          target="pdisc" gi={selection.gi} pi={selection.pi} label="SIZE" />
        <Handle kind="ring" ox={pe.pcx} oy={pe.pcy} r={pe.ring} angDeg={0}
          target="pring" gi={selection.gi} pi={selection.pi} label="ORBIT" />
      </>
    );
  }

  if (selection.kind === 'sun') {
    return <Handle kind="disc" ox={sunX} oy={sunY} r={sunR} angDeg={-45} target="sun" label="SIZE" />;
  }

  return null;
}
